import React, { useEffect, useState } from "react";
import { Trash2, Mail, Phone, Building, Calendar, MessageSquare } from "lucide-react";
import { adminListContacts, adminDeleteContact } from "../../lib/api";
import { toast } from "sonner";

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const reload = () => {
    setLoading(true);
    adminListContacts()
      .then(setItems)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const onDelete = async (c) => {
    if (!window.confirm(`Excluir mensagem de ${c.name}?`)) return;
    try {
      await adminDeleteContact(c.id);
      toast.success("Mensagem excluída");
      setSelected(null);
      reload();
    } catch {
      toast.error("Falha ao excluir");
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-contacts-page">
      <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-3">// MENSAGENS</div>
      <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Contatos recebidos</h1>
      <p className="mt-2 text-sm text-[#6B7280]">Todas as mensagens enviadas pelo formulário do site.</p>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-[#E5E7EB]">
          {loading ? (
            <div className="p-6 text-sm text-[#6B7280]">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-[#6B7280]">Nenhuma mensagem recebida ainda.</div>
          ) : (
            <ul className="divide-y divide-[#E5E7EB] max-h-[70vh] overflow-y-auto">
              {items.map((c) => (
                <li
                  key={c.id}
                  onClick={() => setSelected(c)}
                  data-testid={`admin-contact-row-${c.id}`}
                  className={`p-5 cursor-pointer hover:bg-[#F9F9F9] ${selected?.id === c.id ? "bg-[#F9F9F9] border-l-2 border-[#E84E34]" : ""}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="font-bold text-sm truncate">{c.name}</div>
                    <div className="text-[10px] font-mono-sm tracking-wider text-[#6B7280] whitespace-nowrap">
                      {formatDate(c.created_at)}
                    </div>
                  </div>
                  <div className="text-xs text-[#6B7280] truncate">{c.email}</div>
                  <div className="text-sm text-[#374151] mt-1.5 line-clamp-2">{c.message}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-7">
          {selected ? (
            <div className="bg-white border border-[#E5E7EB] p-6 md:p-8" data-testid="admin-contact-detail">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280]">Remetente</div>
                  <h2 className="font-display text-2xl font-black mt-1">{selected.name}</h2>
                </div>
                <button
                  onClick={() => onDelete(selected)}
                  data-testid="admin-contact-delete"
                  className="p-2 border border-[#E5E7EB] hover:bg-[#E84E34] hover:text-white hover:border-[#E84E34]"
                  aria-label="Excluir"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <Info icon={Mail} label="E-mail">
                  <a href={`mailto:${selected.email}`} className="hover:text-[#E84E34]">{selected.email}</a>
                </Info>
                {selected.phone && (
                  <Info icon={Phone} label="Telefone">{selected.phone}</Info>
                )}
                {selected.company && (
                  <Info icon={Building} label="Empresa">{selected.company}</Info>
                )}
                <Info icon={Calendar} label="Recebido em">{formatDate(selected.created_at, true)}</Info>
              </div>

              {selected.subject && (
                <div className="mb-5">
                  <div className="font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280] mb-1">Assunto</div>
                  <div className="font-bold">{selected.subject}</div>
                </div>
              )}

              <div className="border-t border-[#E5E7EB] pt-5">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare size={16} className="text-[#E84E34]" />
                  <div className="font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280]">Mensagem</div>
                </div>
                <p className="text-[#1A1A1A] whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>

              <div className="mt-8 pt-6 border-t border-[#E5E7EB] flex gap-3">
                <a
                  href={`mailto:${selected.email}?subject=Re: ${selected.subject || 'Seu contato com a Avantech'}`}
                  className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] hover:bg-[#E84E34]"
                >
                  <Mail size={14} /> Responder por e-mail
                </a>
                {selected.phone && (
                  <a
                    href={`https://wa.me/${(selected.phone || '').replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] hover:opacity-90"
                  >
                    <Phone size={14} /> WhatsApp
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-dashed border-[#E5E7EB] p-12 text-center text-[#6B7280]">
              Selecione uma mensagem para ver os detalhes.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({ icon: Icon, label, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} className="text-[#E84E34]" />
      <div className="font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280]">{label}</div>
    </div>
    <div className="text-sm">{children}</div>
  </div>
);

function formatDate(iso, withTime = false) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("pt-BR");
  if (!withTime) return date;
  return `${date} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}
